/**
 * MapComponent
 *
 * Serves as a coordinator between MapLibre rendering,
 * global application state (layers, selection, popups),
 * and domain-specific orchestration hooks.
 *
 * Business logic intentionally lives in hooks.
 * This component focuses on composition and interaction wiring.
 */

import React, { useEffect, useRef, useState, useCallback, useMemo, ReactNode } from "react";
import { Map, type MapRef, Marker, Popup, Source, Layer } from "react-map-gl/maplibre";
import "maplibre-gl/dist/maplibre-gl.css";
// MUI Components
import { Box, CircularProgress, Typography, Paper } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { centroid } from "@turf/turf";

// Contexts and Hooks
import { useConfig } from "../../contexts/ConfigContext";
import { useMapActions, useMapSelector } from "../../store/map";
import { useLayerSelector } from "../../store/layer";
import { usePopupActions, usePopupSelector } from "../../store/popupStore";
import { useFeatureInfoActions, useFeatureInfoSelector } from "../../store/featureInfoStore";
import { useMapFlying } from "../../hooks/useMapFlying";
import { useLayerInitialization } from "../../hooks/useLayerInitialization";
import { useGeolocation } from "../../hooks/useGeolocation";
import { useMapLibreLayers } from "../../hooks/maps/useMapLibreLayers";
import { useMapClickHandler } from "../../hooks/maps/useMapClickHandler";
import { useContextMenu } from "../../hooks/maps/useContextMenu";
import { useMapLayerZOrder, type ManagedLayer } from "../../hooks/maps/useMapLayerZOrder";
import { APP_BASEMAP_STYLE } from "../../utilities/basemap";
import { useSelectedEntity } from "../../hooks/useSelectedEntity";
import { useSelectionActions, useSelectionSelector } from "../../store/selection";

// Components
import Layers from "./layers/Layers";
import LayersPanel from "./layers-panel/LayersPanel";
import MapControls from "./map-controls/MapControls";
import MapPopup from "./popups/MapPopup";
import FeatureInfoPopup from "./popups/FeatureInfoPopup";
import ReactivePopupContent from "./popups/ReactivePopupContent";
import WmsLegendOverlay from "./WmsLegendOverlay";
import { ContextMenu } from "./context-menu/ContextMenu";

// Types and Utilities
import { RasterDemLayerConfig, ApiEndpoint, CogLayerConfig, PMTilesLayerConfig } from "../../types";
import { extractFeatureData } from "../../utilities/dataSourceAdapters";
import { checkValidGeojsonAndSupportedType, SearchablePlaceItem, wktToGeoJSON } from "../../utilities/mapSearchUtils";
import { useCmTableMapLayers, stripCmSubLayerSuffix } from "../../hooks/useCmTableMapLayers";
import { useCmTableBbox } from "../../hooks/useCmTableBbox";
import { isCmTableEndpoint, getCmTableSlugFromEndpoint } from "../../utilities/cmTableEndpoint";
import { useSpatialDatasetMapLayers } from "../../hooks/useSpatialDatasetMapLayers";
import { datasetIdFromSpatialLayerId, isSpatialDatasetLayerId, spatialDatasetLayerId } from "../../constants/spatialDataset";

// Styles
import "./MapComponent.css";
import { useUIActions, useUISelector } from "../../store/UI";
import { getBboxByGeom } from "../../utilities/bboxUtils";
import { isLngLatWithinBounds } from "../../utilities/mapBounds";
import { useMediaSelector } from "../../store/media";
import { hasValidCoordinates } from "../../utilities/geoUtils";
import { getRequiredZoomForBBox } from "../../utilities/mapZoomUtils";
import { attachMissingImageLoader } from "../../utilities/mapImageLoader";
import { boundaryTypeOf } from "../../utilities/boundaryType";
import { isClickableLayer } from "../../utilities/layerInteractivity";
import { isOrgBoundaryLayer } from "../../utilities/boundaryStyle";

interface MapComponentProps {
  isSidePanelOpen: boolean;
  hideControls?: boolean;
}

const FEATURE_PADDING = {
  top: 64,
  bottom: 24,
  left: 24,
  right: 24
};

const SECONDARY_ENTITY_FEATURE_PADDING = {
  ...FEATURE_PADDING,
  top: FEATURE_PADDING?.top + 36 // add additional 36px to the top so that top positioned features visible
};
const DEFAULT_TILT_ANGLE = 60;

function MapControlsWrapper({ children }: { children: ReactNode }) {
  const { isMediaDetailOpen } = useMediaSelector();
  const { isMediaDrawerVisible } = useUISelector();
  const bottomPosition = isMediaDrawerVisible && isMediaDetailOpen ? "0.5em" : "2em";
  return <Box sx={{ position: "absolute", bottom: bottomPosition, right: "1rem" }}>{children}</Box>;
}

/**
 * How long a flyTo waits for its dataset's source to report loaded before it
 * clears the spinner anyway. Long enough for a cold COG/PMTiles range request,
 * short enough that a source which never mounts does not wedge the UI.
 */
const SPATIAL_LOAD_FALLBACK_MS = 20_000;

const MapComponent: React.FC<MapComponentProps> = ({ isSidePanelOpen, hideControls = false }) => {
  // ---- Refs ----
  const mapRef = useRef<MapRef | null>(null);
  // Flipped true once the map's `load` event fires so effects that depend on a live map
  // (e.g. useMapLayerZOrder) reliably re-run — on first render mapRef.current is still null.
  const [mapReady, setMapReady] = useState(false);
  const rotationIntervalRef = useRef<number | null>(null);
  // True while a programmatic popup-viewport-clamp pan (ensureGeoPopupInViewport) is animating.
  // Used to (a) prevent the clamp's own `moveend` from re-triggering the clamp (feedback loop)
  // and (b) suppress the bbox-debounce "Processing Layer" indicator for these tiny pans.
  const clampPanningRef = useRef(false);

  // Global State from Contexts and Stores
  const config = useConfig();
  const theme = useTheme();
  const { entity: selectedEntity, isLoading, isFetchingRelationships } = useSelectedEntity();
  const {
    showLayerPanel,
    cmTableBbox,
    showOnlySelectedEndpointsDataOnMap,
    isCmTableLoading,
    isMapBboxDebouncing,
    isSpatialDatasetLoading,
    pendingFlyTo,
    coordinateSearchMarker
  } = useMapSelector();
  const { toggleLayerPanel, setCommonMapState, setPendingFlyTo, setCoordinateSearchMarker } = useMapActions();
  const { layers, terrainLayers, visibleLayers } = useLayerSelector();
  const { isOpen, coords: storedCoords, popupType, geoPopupLayoutRevision, isFullViewerOpen } = usePopupSelector();
  const { closePopup, openNonGeoPopup } = usePopupActions();
  const { features: inspectedFeatures, activeIndex: inspectedIndex, sourceName: inspectedSourceName } = useFeatureInfoSelector();
  const { stepFeature, clear: clearInspectedFeatures } = useFeatureInfoActions();
  const { selectedEndpoint, selectedEntityId, secondaryItemsBbox } = useSelectionSelector();
  const { setSelectedPrimaryStates } = useSelectionActions();
  const { shouldClosePopups, shouldZoomToHome, showSidebar, sidebarWidth } = useUISelector();
  const { resetClosePopupsFlag, resetZoomToHomeFlag } = useUIActions();

  // Custom Hooks
  const { flyTo } = useMapFlying(mapRef);
  const { mapLibreLayers: baseMapLibreLayers } = useMapLibreLayers();

  // CM Table endpoints derived from config
  const cmTableEndpoints = useMemo(() => (config.apiEndpoints ?? []).filter(isCmTableEndpoint), [config.apiEndpoints]);

  // Derive the slug of the currently selected CM Table endpoint.
  const activeTableSlug = useMemo(() => {
    return getCmTableSlugFromEndpoint(selectedEndpoint);
  }, [selectedEndpoint]);

  // Debounced viewport bbox – writes to map store, readable by SideBar via useMapSelector().
  // Pass clampPanningRef so programmatic popup-clamp pans don't flip the loading indicator.
  useCmTableBbox(mapRef, 500, clampPanningRef);

  const { layers: cmTableMapLayers, selectedTableExtent } = useCmTableMapLayers({
    cmTableEndpoints,
    activeTableSlug,
    bbox: cmTableBbox,
    showOnlyFilter: showOnlySelectedEndpointsDataOnMap,
    defaultColor: theme.palette.primary.main
  });

  // Merge static config layers with dynamic CM Table GeoJSON layers
  const mapLibreLayers = useMemo(() => [...baseMapLibreLayers, ...cmTableMapLayers], [baseMapLibreLayers, cmTableMapLayers]);

  // `/api/v2/config/` sends only the boundary's derived label; the raw
  // `boundary_type` travels in the GeoJSON's feature properties, which the layer
  // fetch has already pulled down by the time this resolves. Used only to name
  // the boundary's row in the layers panel.
  const orgBoundaryType = useMemo(
    () => boundaryTypeOf((mapLibreLayers.find((layer: any) => isOrgBoundaryLayer(layer)) as any)?.source?.data),
    [mapLibreLayers]
  );

  // No-op — spatial layers are registered on demand via SideBar.handleSelectEntity
  useSpatialDatasetMapLayers();

  // Active spatial layer rendered directly (not via useMapLibreLayers) to avoid
  // the reset/rebuild cycle that freezes the map on dataset selection (§7.5).
  const spatialOverlays = useMemo(
    () =>
      layers.filter((l) => isSpatialDatasetLayerId(l.id) && (l.type === "cog" || l.type === "pmtiles")) as (
        | CogLayerConfig
        | PMTilesLayerConfig
      )[],
    [layers]
  );
  const visibleSpatialOverlays = useMemo(() => spatialOverlays.filter((l) => visibleLayers.has(l.id)), [spatialOverlays, visibleLayers]);

  // Canonical id of the layer the user last selected — always lifted to the top of the map.
  const activeLayerId = useMemo(() => {
    if (!selectedEndpoint) return null;
    if (selectedEndpoint.entityType === "spatial-dataset" && selectedEntityId != null) {
      return spatialDatasetLayerId(selectedEntityId);
    }
    return selectedEndpoint.mapLayerId || null;
  }, [selectedEndpoint, selectedEntityId]);

  // All overlay layers currently mounted on the map, tagged with their MapLibre render type so
  // useMapLayerZOrder can enforce Base -> Raster -> Polygon -> Line -> Point (active layer on top).
  const managedLayers = useMemo<ManagedLayer[]>(() => {
    const list: ManagedLayer[] = mapLibreLayers.map((l) => ({ id: l.id, type: l.type }));
    visibleSpatialOverlays.forEach((layer) => {
      const spatialType =
        layer.type === "cog"
          ? "raster"
          : (layer as PMTilesLayerConfig).layerFormat === "raster"
            ? "raster"
            : (layer as PMTilesLayerConfig).style?.type || "fill";
      list.push({ id: layer.id, type: spatialType });
    });
    return list;
  }, [mapLibreLayers, visibleSpatialOverlays]);

  // Enforce the map layer stacking order imperatively (react-map-gl won't reorder mounted layers).
  useMapLayerZOrder(mapRef, managedLayers, activeLayerId, mapReady);

  // Tracks user geolocation and manages map centering on current position
  const { userLocation, isTrackingLocation, isGettingLocation, handleLocationRequest } = useGeolocation(mapRef);

  const { handleMapRightClick, contextMenu, handleClose, handleCopyCoordinates } = useContextMenu();

  // Local State
  const [isOrbiting, setIsOrbiting] = useState(false);
  const [_, setSearchableItemsData] = useState<Record<string, SearchablePlaceItem[]>>({});

  // Terrain Layer Reference
  const terrainLayer = terrainLayers[0] as RasterDemLayerConfig; // Choose the first terrain layer from any provided

  // Layer Initialization
  useLayerInitialization();

  // Effect: Listen for popup closure trigger and close all popups
  useEffect(() => {
    // don't close if popup is already open
    if (popupType?.trim()) return;

    if (shouldClosePopups) {
      console.log("[MapComponent] Closing all popups due to trigger");

      // Close MapLibre popup
      closePopup();

      // Reset the flag
      resetClosePopupsFlag();
    }
  }, [shouldClosePopups, closePopup, resetClosePopupsFlag, popupType]);

  // Effect: Listen for zoom to home trigger and zoom to initial view
  useEffect(() => {
    if (shouldZoomToHome) {
      console.log("[MapComponent] Zooming to home due to trigger");

      // Use the existing home click handler logic
      handleHomeClick();

      // Reset the flag
      resetZoomToHomeFlag();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldZoomToHome, resetZoomToHomeFlag]);

  // Effect: Extract searchable places from loaded map layers (similar to how DataDrawer searches loaded data)
  useEffect(() => {
    const extractSearchableData = async () => {
      if (!mapRef.current) return;

      const map = mapRef.current.getMap();
      if (!map || !map.isStyleLoaded()) return;

      // Find the places layer (which loads all places data already)
      const placesLayer = layers.find((layer) => layer.searchable && layer.searchableFields && layer.id === "Dambi Places");

      if (!placesLayer) return;

      try {
        // Query all features from the places layer
        const features = map.querySourceFeatures(placesLayer.id) || [];

        if (features.length > 0) {
          console.log(`[MapComponent] Extracted ${features.length} places from map layer for local search`);

          // Transform to searchable format using adapter system
          const searchableItems: SearchablePlaceItem[] = features.map((feature: any) => {
            // Find the endpoint for this layer to determine data source type
            const layerEndpoint = config?.apiEndpoints?.find(
              (ep) => ep.mapLayerId === placesLayer.id || ep.tableName.toLowerCase() === placesLayer.id.toLowerCase()
            );

            // Use adapter system for feature extraction
            const featureData = extractFeatureData(feature.properties, placesLayer.id, layerEndpoint);
            const featureId = featureData.id;

            return {
              id: featureId,
              name: feature.properties?.name || `${placesLayer.id} Feature`,
              description: feature.properties?.description || "",
              coordinates: feature.geometry?.type === "Point" ? (feature.geometry.coordinates as [number, number]) : undefined,
              geometry: feature.geometry,
              layerId: placesLayer.id,
              properties: feature.properties || {}
            };
          });

          setSearchableItemsData({ [placesLayer.id]: searchableItems });
        }
      } catch (error) {
        console.warn("[MapComponent] Could not extract places from map layer:", error);
        // Fallback to empty search data
        setSearchableItemsData({});
      }
    };

    extractSearchableData();
  }, [layers, mapLibreLayers, config]);

  // Effect: Handle culturemap:flyTo events dispatched by SideBar when a spatial
  // dataset is selected — flies to its bbox and clears the loading state once idle.
  // skipIfCenterWithinBounds is the §15.2 camera-plan rule: already looking at
  // the layer (e.g. identify click remounting the dataset card) must not yank
  // the camera back to the full extent.
  useEffect(() => {
    // Watchers armed by a flyTo but not yet satisfied, so unmounting (or a newer
    // flyTo) can tear them down instead of leaving listeners on the map.
    const pendingLoadWatchers = new Set<() => void>();

    const handleFlyToEvent = (e: Event) => {
      const customEvent = e as CustomEvent;
      const { layerId: awaitedLayerId, bounds, center, zoom, clearSpatialLoading, skipIfCenterWithinBounds } = customEvent.detail;
      const map = mapRef.current?.getMap();
      if (!map) return;

      if (bounds && skipIfCenterWithinBounds && isLngLatWithinBounds(map.getCenter(), bounds)) {
        if (clearSpatialLoading) setCommonMapState({ isSpatialDatasetLoading: false });
        return;
      }

      if (bounds) {
        map.fitBounds(bounds, { padding: FEATURE_PADDING, duration: 2000 });
      } else if (center) {
        map.flyTo({ center, zoom: zoom || 10, duration: 2000 });
      }

      if (clearSpatialLoading) {
        // Only the newest flyTo matters; drop any watcher still waiting on the
        // dataset the user has just navigated away from.
        pendingLoadWatchers.forEach((disposeWatcher) => disposeWatcher());

        // `idle` can stall with COG/PMTiles custom protocols; use sourcedata instead.
        let fallbackTimer: ReturnType<typeof setTimeout> | null = null;

        const dispose = () => {
          map.off("sourcedata", offInitialLoad);
          if (fallbackTimer) clearTimeout(fallbackTimer);
          fallbackTimer = null;
          pendingLoadWatchers.delete(dispose);
        };

        const offInitialLoad = (e: any) => {
          if (!e.isSourceLoaded) return;
          // Wait for the overlay this flyTo was dispatched for. Several datasets
          // can sit on the map at once, so an already-loaded one would otherwise
          // satisfy this callback before the newly selected overlay has fetched a
          // single tile. Untagged events (older dispatchers) keep the old
          // any-spatial-source behaviour.
          if (awaitedLayerId ? e.sourceId !== awaitedLayerId : !isSpatialDatasetLayerId(e.sourceId)) return;
          dispose();
          setCommonMapState({ isSpatialDatasetLoading: false });
        };

        // Backstop: a source that never mounts — still processing, dead URL —
        // must not leave the spinner up for good now that we wait on one id.
        fallbackTimer = setTimeout(() => {
          dispose();
          setCommonMapState({ isSpatialDatasetLoading: false });
        }, SPATIAL_LOAD_FALLBACK_MS);

        map.on("sourcedata", offInitialLoad);
        pendingLoadWatchers.add(dispose);
      }
    };

    window.addEventListener("culturemap:flyTo", handleFlyToEvent);
    return () => {
      window.removeEventListener("culturemap:flyTo", handleFlyToEvent);
      pendingLoadWatchers.forEach((disposeWatcher) => disposeWatcher());
      pendingLoadWatchers.clear();
    };
  }, [setCommonMapState]);

  // Safety net: whenever no spatial overlay is visible, immediately clear the
  // loading indicator — covers cases where the last overlay is hidden while a
  // movestart-triggered load is still in progress.
  useEffect(() => {
    if (visibleSpatialOverlays.length === 0) {
      setCommonMapState({ isSpatialDatasetLoading: false });
    }
  }, [visibleSpatialOverlays.length, setCommonMapState]);

  // Track COG / PMTiles tile loading so "Processing Layer" shows while the
  // source is actively fetching tiles and clears once all are loaded.
  //
  // WHY sourcedataloading / sourcedata (not render or idle):
  //   MapLibre renders only when it has NEW pixel data to draw. While HTTP range
  //   requests are in-flight (206 Partial Content) MapLibre has nothing new to
  //   draw, so render stops firing even though tiles have not arrived yet. A
  //   render-trailing debounce therefore fires too early.
  //   sourcedataloading fires once per tile-URL when MapLibre starts a request.
  //   sourcedata fires when a tile finishes; isSourceLoaded=true when ALL done.
  //   Together they track actual network activity rather than screen repaints.
  useEffect(() => {
    if (visibleSpatialOverlays.length === 0) return;
    const map = mapRef.current?.getMap();
    if (!map) return;

    const sourceIds = new Set(visibleSpatialOverlays.map((l) => l.id));
    let clearTimer: ReturnType<typeof setTimeout> | null = null;

    const cancelClear = () => {
      if (clearTimer) {
        clearTimeout(clearTimer);
        clearTimer = null;
      }
    };

    // A tile request started — keep (or restore) the spinner.
    const handleSourceDataLoading = (e: any) => {
      if (!sourceIds.has(e.sourceId)) return;
      cancelClear();
      setCommonMapState({ isSpatialDatasetLoading: true });
    };

    // Every visible overlay has finished fetching — `e.isSourceLoaded` speaks only
    // for the source that fired, so with several overlays up it would clear the
    // spinner while the others are still pulling tiles.
    const allOverlaysLoaded = () =>
      [...sourceIds].every((id) => {
        try {
          return !map.getSource(id) || map.isSourceLoaded(id);
        } catch {
          // Source torn down mid-pass — treat it as nothing left to wait for.
          return true;
        }
      });

    // A tile finished loading or the source state changed.
    const handleSourceData = (e: any) => {
      if (!sourceIds.has(e.sourceId)) return;
      if (e.isSourceLoaded && allOverlaysLoaded()) {
        // All tiles are in cache — schedule the clear with a short debounce.
        cancelClear();
        clearTimer = setTimeout(() => {
          clearTimer = null;
          setCommonMapState({ isSpatialDatasetLoading: false });
        }, 300);
      } else {
        // More tiles still loading — cancel any pending clear.
        cancelClear();
      }
    };

    map.on("sourcedataloading", handleSourceDataLoading);
    map.on("sourcedata", handleSourceData);
    return () => {
      map.off("sourcedataloading", handleSourceDataLoading);
      map.off("sourcedata", handleSourceData);
      cancelClear();
    };
  }, [visibleSpatialOverlays, setCommonMapState]);

  // Effect: Update terrain exaggeration when it changes in config
  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map || !config) return;

    // Check if terrainExaggeration exists and terrain source is available
    if (config.terrainExaggeration !== undefined && terrainLayer && map.getSource(terrainLayer.id)) {
      // Set the terrain with the new exaggeration value
      map.setTerrain({
        source: terrainLayer.id,
        exaggeration: config.terrainExaggeration !== undefined ? config.terrainExaggeration : terrainLayer.options.exaggeration
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.terrainExaggeration, terrainLayer]);

  // Finds the API endpoint config for a given map layer ID.
  // Used by the click handler to resolve which endpoint to open a popup for.
  // Also searches CM Table endpoints so clicks on cm-table-* layers work.
  const findEndpointByLayerId = useCallback(
    (layerId: string) => {
      // Mixed-geometry CM tables click through sibling layers (…::line / …::fill);
      // normalise to the canonical cm-table-<slug> id before matching.
      const resolvedId = stripCmSubLayerSuffix(layerId);
      return (
        config.apiEndpoints?.find(
          (ep) =>
            ep.mapLayerId === resolvedId ||
            ep.tableName.toLowerCase() === resolvedId.toLowerCase() ||
            ep.name.toLowerCase() === resolvedId.toLowerCase() ||
            (isCmTableEndpoint(ep) && `cm-table-${ep.tableSlug || ep.tableName}` === resolvedId)
        ) || null
      );
    },
    [config?.apiEndpoints]
  );

  const interactiveLayerIds = useMemo(
    () => [
      ...layers.filter(isClickableLayer).map((l) => l.id),
      ...cmTableMapLayers.filter(isClickableLayer).map((l: any) => l.id)
    ],
    [layers, cmTableMapLayers]
  );

  const addMissingImages = useCallback((map: any) => attachMissingImageLoader(map), []);

  // Event Handlers
  const onMapClick = useMapClickHandler({
    mapRef,
    layers,
    config,
    findEndpointByLayerId
  });

  /**
   * Raster spatial layers (COG / raster PMTiles) are not queryable — MapLibre never
   * returns features for them, so the feature-based path in useMapClickHandler is
   * never reached. When the user clicks on the map with no feature hit but a raster
   * spatial overlay is visible, check whether the click falls inside a layer's bounds
   * and re-open the corner card for that dataset.
   */
  const handleMapClick = useCallback(
    async (e: any) => {
      if (!e.features || e.features.length === 0) {
        const { lng, lat } = e.lngLat;
        // Topmost overlay wins. Array order is not draw order — useMapLayerZOrder
        // lifts the active layer above overlays added after it — so rank against
        // the map's own stack and only fall back to the array when it is not up.
        const drawOrder = mapRef.current?.getMap?.()?.getLayersOrder?.();
        const candidates = drawOrder?.length
          ? [...visibleSpatialOverlays].sort((a, b) => drawOrder.indexOf(b.id) - drawOrder.indexOf(a.id))
          : [...visibleSpatialOverlays].reverse();
        const hit = candidates.find((layer) => {
          const isRaster = layer.type === "cog" || (layer.type === "pmtiles" && (layer as PMTilesLayerConfig).layerFormat === "raster");
          if (!isRaster) return false;
          const bounds = layer.bounds;
          return !bounds || (lng >= bounds[0][0] && lng <= bounds[1][0] && lat >= bounds[0][1] && lat <= bounds[1][1]);
        });

        if (hit && !isOpen) {
          const datasetId = datasetIdFromSpatialLayerId(hit.id);
          if (selectedEntityId && String(selectedEntityId) === datasetId) {
            openNonGeoPopup();
            return;
          }
          if (datasetId) {
            const spatialEndpoint = config.apiEndpoints?.find((ep: any) => ep.entityType === "spatial-dataset");
            if (spatialEndpoint) {
              setSelectedPrimaryStates({
                selectedEndpoint: spatialEndpoint,
                selectedEntityId: datasetId,
                showRelationShip: false
              });
              openNonGeoPopup();
            }
            return;
          }
        }
      }
      return onMapClick(e);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [visibleSpatialOverlays, selectedEntityId, isOpen, onMapClick, openNonGeoPopup, setSelectedPrimaryStates, config.apiEndpoints]
  );

  // Inspected features are click-transient, so they go when the popup does.
  const handleCloseFeatureInfo = useCallback(() => {
    clearInspectedFeatures();
    closePopup();
  }, [clearInspectedFeatures, closePopup]);

  const handle3DToggle = () => {
    if (mapRef.current) {
      const map = mapRef.current.getMap();
      const newPitch = map.getPitch() === 0 ? 60 : 0;
      // Use regular easeTo for 3D toggle (don't close popups - it's just a view change)
      map.easeTo({ pitch: newPitch });
    }
  };

  const toggleOrbit = useCallback(() => {
    if (isOrbiting) {
      // Stop orbiting
      if (rotationIntervalRef.current) {
        window.clearInterval(rotationIntervalRef.current);
        rotationIntervalRef.current = null;
      }
    } else {
      // Start orbiting
      if (mapRef.current) {
        const map = mapRef.current.getMap();
        rotationIntervalRef.current = window.setInterval(() => {
          const currentBearing = map.getBearing();
          map.easeTo({
            bearing: currentBearing + 0.5,
            duration: 50,
            easing: (t: number) => t
          });
        }, 50);
      }
    }
    setIsOrbiting(!isOrbiting);
  }, [isOrbiting]);

  const handleHomeClick = useCallback(() => {
    if (mapRef.current) {
      const initialView = config.initialMapView;

      // Use the centralized flying method (automatically closes popups)
      flyTo({
        center: [initialView.longitude, initialView.latitude],
        zoom: initialView.zoom,
        pitch: initialView.pitch,
        bearing: 0, // Reset bearing to north
        duration: 2000 // 2 seconds animation
      });

      // Note: 3D mode state is managed by the pitch value itself

      // Stop orbiting if it's active
      if (isOrbiting) {
        toggleOrbit();
      }
    }
  }, [config.initialMapView, flyTo, isOrbiting, toggleOrbit]);

  // Fly to coordinate search target dispatched from SideBar search bar
  useEffect(() => {
    if (!pendingFlyTo || !mapRef.current) return;
    flyTo({
      center: [pendingFlyTo.lng, pendingFlyTo.lat],
      zoom: pendingFlyTo.zoom,
      duration: 2000
    });
    setPendingFlyTo(null);
  }, [pendingFlyTo, flyTo, setPendingFlyTo]);

  // function to handle zoom increment
  const handleZoomIn = () => {
    if (mapRef.current) {
      const map = mapRef.current.getMap();
      const currentZoom = map.getZoom();
      map.easeTo({ zoom: currentZoom + 1 });
    }
  };

  // function to handle zoom decrement
  const handleZoomOut = () => {
    if (mapRef.current) {
      const map = mapRef.current.getMap();
      const currentZoom = map.getZoom();
      map.easeTo({ zoom: currentZoom - 1 });
    }
  };

  // Unify zooming to Selected Entity OR Secondary Items Bbox
  useEffect(() => {
    const map = mapRef.current?.getMap();
    // NOTE: intentionally NOT gated on `isFetchingRelationships`. For far-away
    // selections (e.g. Nepal default view -> an Australian feature) waiting for
    // relationships to finish made the camera appear "stuck" until the fetch
    // resolved. We fly to the selected entity's own geometry as soon as it is
    // available; the secondary-items bbox refinement (when enabled) still runs
    // later via the `secondaryItemsBbox` dependency.
    if (!map || isLoading) return;

    // Spatial datasets fly to their own bounds via the `culturemap:flyTo` custom
    // event dispatched by SideBar — skip so this effect doesn't override with a
    // maxZoom:12 fitBounds.
    if (selectedEndpoint?.entityType === "spatial-dataset") return;

    // If we have secondary items, that's our priority.
    // Otherwise, use the selected entity.
    // If neither exists, we are in a "reset" state.
    const useRelatedItemsZoom = config.enableZoomToRelatedItems === true;
    const hasSecondaryBbox = useRelatedItemsZoom && secondaryItemsBbox && hasValidCoordinates(secondaryItemsBbox);
    const hasEntityGeom = selectedEntity?.geometry;

    // Handle Reset/Clear Selection
    // We use a 200ms grace period before flying home. This prevents "movement"
    // when switching features (where selectedEntityId is null for ~100ms).
    // Exception: if the selected endpoint is a CM Table, don't fly home — the
    // layer data will arrive shortly and trigger its own flyTo.
    if (!hasSecondaryBbox && !hasEntityGeom && !selectedEntityId) {
      if (activeTableSlug) return; // CM Table selected but entity not picked – stay put
      const timer = setTimeout(() => {
        handleHomeClick();
      }, 200);
      return () => clearTimeout(timer);
    }

    // If an entity is being fetched (ID set but geometry not yet loaded), wait.
    if (!hasSecondaryBbox && !hasEntityGeom && selectedEntityId) return;

    const shouldTilt = config?.tiltMapOnPointItemSelect && selectedEntity?.geometry?.type === "Point";

    // 2. Determine Geometry and Settings
    const targetBbox = hasSecondaryBbox ? (secondaryItemsBbox as any) : getBboxByGeom(selectedEntity!.geometry);

    if (!hasValidCoordinates(targetBbox)) return;

    // Configure settings based on target type
    const padding = hasSecondaryBbox ? SECONDARY_ENTITY_FEATURE_PADDING : FEATURE_PADDING;
    const maxZoomLimit = hasSecondaryBbox ? 10 : 12;
    const animDuration = hasSecondaryBbox ? 1500 : 3000;

    // 3. One single timeout to wait for Fetching & CSS
    const timer = setTimeout(() => {
      // Cancel any in-flight camera animation so a new long-distance fitBounds
      // starts cleanly instead of fighting a previous transition.
      map.stop();
      const zoomInfo = getRequiredZoomForBBox({ map: mapRef.current!, bbox: targetBbox });
      const finalZoom = Math.min(maxZoomLimit, zoomInfo?.zoom || 12);

      mapRef.current?.fitBounds(
        [
          [targetBbox[0], targetBbox[1]],
          [targetBbox[2], targetBbox[3]]
        ],
        {
          pitch: shouldTilt ? DEFAULT_TILT_ANGLE : 0,
          bearing: 0,
          duration: animDuration,
          padding: padding,
          center: zoomInfo?.center,
          maxZoom: finalZoom
        }
      );
    }, 1000);

    // 4. Cleanup: Cancel whichever transition was pending
    return () => clearTimeout(timer);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEntityId, selectedEntity?.geometry, secondaryItemsBbox, isLoading, config.enableZoomToRelatedItems]);

  // Effect: Fly to the full extent of a CM Table when it's first loaded (Track A)
  useEffect(() => {
    if (!selectedTableExtent || !hasValidCoordinates(selectedTableExtent)) return;

    const timer = setTimeout(() => {
      mapRef.current?.fitBounds(
        [
          [selectedTableExtent[0], selectedTableExtent[1]],
          [selectedTableExtent[2], selectedTableExtent[3]]
        ],
        {
          padding: FEATURE_PADDING,
          duration: 3000,
          maxZoom: 12
        }
      );
    }, 500);

    return () => clearTimeout(timer);
  }, [selectedTableExtent]);
  // If the handleHomeClick is added it interrupt the map initialization and forces to fly to center so map freezes

  const mediaMarkerCoords = useMemo(() => {
    if (popupType !== "media-viewer" || !selectedEntity) return null;
    if (selectedEntity.coordinates) {
      const [lng, lat] = selectedEntity.coordinates as [number, number];
      if (typeof lng === "number" && typeof lat === "number") return { lng, lat };
    }
    if (selectedEntity.geometry) {
      try {
        const geo =
          typeof selectedEntity.geometry === "object"
            ? checkValidGeojsonAndSupportedType(selectedEntity.geometry)
            : wktToGeoJSON(selectedEntity.geometry as string);
        if (geo) {
          const c = centroid(geo as any);
          return { lng: c.geometry.coordinates[0], lat: c.geometry.coordinates[1] };
        }
      } catch {
        // invalid geometry — no marker
      }
    }
    return null;
  }, [popupType, selectedEntity]);

  /**
   * Derive popup coordinates at render time
   *
   * Priority:
   *  1. storedCoords — set from map click event (e.lngLat), always available immediately.
   *  2. entity coords — used when popup opened from sidebar/URL where click coords don't exist.
   *
   */
  const popupCoords = useMemo(() => {
    if (popupType !== "geo-entity") return null;
    if (storedCoords) return storedCoords;
    if (!selectedEntity) return null;

    // Derive from entity data (sidebar / URL deep-link case)
    if (selectedEntity.coordinates) {
      const [lng, lat] = selectedEntity.coordinates as [number, number];
      if (lng && lat) return { lng, lat };
    }
    if (selectedEntity.geometry) {
      try {
        const geo =
          typeof selectedEntity.geometry === "object"
            ? checkValidGeojsonAndSupportedType(selectedEntity.geometry)
            : wktToGeoJSON(selectedEntity.geometry as string);
        if (geo) {
          const c = centroid(geo as any);
          return { lng: c.geometry.coordinates[0], lat: c.geometry.coordinates[1] };
        }
      } catch {
        // geometry invalid — popup won't show
      }
    }
    return null;
  }, [popupType, storedCoords, selectedEntity]);

  /**
   * Pan map so the MapLibre popup stays inside the map container (popupStore geoPopupLayoutRevision).
   * Uses project/unproject + easeTo — panBy's internal offset negation made vertical correction wrong
   * when expanding geo popup details (e.g. (i) panel), producing large erroneous southward pans.
   */
  const ensureGeoPopupInViewport = useCallback(() => {
    const map = mapRef.current?.getMap();
    if (!map?.isStyleLoaded?.()) return;
    // A clamp pan is already animating — don't stack another easeTo on top of it.
    // Its trailing moveend will re-measure once and correct any remainder.
    if (clampPanningRef.current) return;
    const container = map.getContainer();
    const popupEl = container.querySelector(".maplibregl-popup") as HTMLElement | null;
    if (!popupEl) return;

    const pad = 12;
    const cr = container.getBoundingClientRect();
    const pr = popupEl.getBoundingClientRect();
    const leftPad = cr.left + pad;
    const rightPad = cr.right - pad;
    const topPad = cr.top + pad;
    const bottomPad = cr.bottom - pad;

    let shiftX = 0;
    let shiftY = 0;
    if (pr.left < leftPad) shiftX += leftPad - pr.left;
    if (pr.right > rightPad) shiftX -= pr.right - rightPad;
    if (pr.top < topPad) shiftY += topPad - pr.top;
    if (pr.bottom > bottomPad) shiftY -= pr.bottom - bottomPad;

    // Ignore sub-threshold corrections. Subpixel positioning + easing can otherwise leave a
    // 1–2px residual that re-triggers easeTo on every moveend, producing an endless micro-pan
    // loop that keeps the map "busy" (and the Processing Layer indicator on).
    const MIN_SHIFT = 8;
    if (Math.abs(shiftX) < MIN_SHIFT) shiftX = 0;
    if (Math.abs(shiftY) < MIN_SHIFT) shiftY = 0;

    if (shiftX === 0 && shiftY === 0) return;

    // If the popup is taller than the viewport, corrections fight each other — cap so we don't fly away.
    // The popup height is now bounded (see DataItemDetails maxHeight), so allow a larger pan than the
    // old fixed 280px cap — otherwise a popup opening near the top can't be fully pushed clear of the
    // navbar. Scale with the visible map height while keeping a sane ceiling.
    const maxShift = Math.max(280, Math.round(cr.height * 0.75));
    shiftX = Math.max(-maxShift, Math.min(maxShift, shiftX));
    shiftY = Math.max(-maxShift, Math.min(maxShift, shiftY));

    const center = map.getCenter();
    const centerPx = map.project(center);
    const next = map.unproject([centerPx.x - shiftX, centerPx.y - shiftY]);

    // Flag this as a programmatic clamp pan so the trailing moveend neither re-triggers the
    // clamp nor flips the bbox-debounce loading indicator. Cleared after moveend (deferred to
    // the next tick so other synchronous moveend listeners still observe the flag as true).
    clampPanningRef.current = true;
    map.once("moveend", () => {
      window.setTimeout(() => {
        clampPanningRef.current = false;
      }, 0);
    });
    map.easeTo({ center: next, duration: 220, essential: true });
  }, []);

  useEffect(() => {
    if (!isOpen || popupType !== "geo-entity" || !popupCoords) return;
    const id = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        ensureGeoPopupInViewport();
      });
    });
    return () => window.cancelAnimationFrame(id);
  }, [geoPopupLayoutRevision, isOpen, popupType, popupCoords, showSidebar, sidebarWidth, ensureGeoPopupInViewport]);

  // Timeline-driven entity switches can render popup DOM/content asynchronously.
  // Retry clamping briefly so newly opened/replaced popups settle into the visible area.
  useEffect(() => {
    if (!isOpen || popupType !== "geo-entity") return;

    let cancelled = false;
    const timerIds: number[] = [];
    const maxAttempts = 8;

    const runClampAttempt = (attempt: number) => {
      if (cancelled) return;
      ensureGeoPopupInViewport();
      if (attempt >= maxAttempts) return;
      const id = window.setTimeout(() => runClampAttempt(attempt + 1), 90);
      timerIds.push(id);
    };

    runClampAttempt(1);

    return () => {
      cancelled = true;
      timerIds.forEach((id) => window.clearTimeout(id));
    };
  }, [isOpen, popupType, selectedEntityId, popupCoords, geoPopupLayoutRevision, ensureGeoPopupInViewport]);

  // fitBounds (e.g. related-item extent when enableZoomToRelatedItems) runs on a timer and can run
  // after the layout bump above — it moves the camera and leaves the popup clipped. Re-run clamp
  // whenever the map camera finishes moving so geo popups stay inside the map container.
  useEffect(() => {
    if (!isOpen || popupType !== "geo-entity") return;
    const map = mapRef.current?.getMap();
    if (!map) return;

    const onMoveEnd = () => {
      window.requestAnimationFrame(() => {
        ensureGeoPopupInViewport();
      });
    };

    map.on("moveend", onMoveEnd);
    return () => {
      map.off("moveend", onMoveEnd);
    };
  }, [isOpen, popupType, ensureGeoPopupInViewport]);

  // Keep the geo popup fully visible when its OWN size changes late (e.g. a fulcrum image
  // finishes loading and the media area appears, growing the popup upward behind the navbar)
  // or when the MAP CONTAINER resizes (opening/closing the fulcrum timeline shrinks the map
  // from the top; sidebar toggles and window resizes also apply). The revision-bump/retry
  // effects above only fire on React state changes and can miss these async/CSS-driven cases.
  useEffect(() => {
    if (!isOpen || popupType !== "geo-entity") return;
    const map = mapRef.current?.getMap();
    if (!map) return;
    const container = map.getContainer();

    let raf = 0;
    const schedule = () => {
      cancelAnimationFrame(raf);
      raf = window.requestAnimationFrame(() => ensureGeoPopupInViewport());
    };

    const observers: ResizeObserver[] = [];

    // Container resize → timeline open/close, sidebar, window. The 0.35s CSS transition emits
    // many resize ticks; the clamp's in-progress guard + min-shift threshold keep this smooth.
    const containerRO = new ResizeObserver(schedule);
    containerRO.observe(container);
    observers.push(containerRO);

    // The popup element is created by MapLibre only after the popup opens, so it may not exist
    // on first run. Attach as soon as it appears, then observe its size (image/info growth).
    let popupRO: ResizeObserver | null = null;
    let pollId: number | null = null;
    const attachPopupObserver = (): boolean => {
      const popupEl = container.querySelector(".maplibregl-popup") as HTMLElement | null;
      if (!popupEl) return false;
      popupRO = new ResizeObserver(schedule);
      popupRO.observe(popupEl);
      observers.push(popupRO);
      // Correct position once now that we can measure the real popup.
      schedule();
      return true;
    };

    if (!attachPopupObserver()) {
      let tries = 0;
      pollId = window.setInterval(() => {
        if (attachPopupObserver() || ++tries > 20) {
          if (pollId !== null) window.clearInterval(pollId);
          pollId = null;
        }
      }, 50);
    }

    return () => {
      cancelAnimationFrame(raf);
      if (pollId !== null) window.clearInterval(pollId);
      observers.forEach((o) => o.disconnect());
    };
  }, [isOpen, popupType, selectedEntityId, popupCoords, ensureGeoPopupInViewport]);

  return (
    <Box
      className='mapContainer'
      sx={{
        position: "relative",
        width: showSidebar ? `calc(100vw - ${sidebarWidth}px)` : "100vw",
        marginLeft: showSidebar ? `${sidebarWidth}px` : 0,
        height: "100%",
        minHeight: "100%" // Ensure it fills the full container height
      }}
    >
      {/* Map controls---- Refactored and moved to a separate component ---- */}
      {!hideControls && (
        <MapControlsWrapper>
          <MapControls
            onHomeClick={handleHomeClick}
            on3DToggle={handle3DToggle}
            onLocationRequest={handleLocationRequest}
            onLayerToggle={toggleLayerPanel}
            onZoomIn={handleZoomIn}
            onZoomOut={handleZoomOut}
            isTrackingLocation={isTrackingLocation}
            isGettingLocation={isGettingLocation}
            isOrbiting={isOrbiting}
            toggleOrbit={toggleOrbit}
          />
        </MapControlsWrapper>
      )}
      <Map
        // Explicit styles required for reliable rendering in flex/relative containers
        style={{ width: "100%", height: "100%" }}
        // Removed dynamic key to prevent unnecessary unmounting/remounting
        ref={mapRef}
        initialViewState={config.initialMapView}
        mapStyle={APP_BASEMAP_STYLE}
        attributionControl={false}
        maxPitch={65}
        // reuseMaps // Commented out to prevent potential texture/context recycling issues
        onClick={handleMapClick}
        onContextMenu={handleMapRightClick}
        interactiveLayerIds={interactiveLayerIds}
        onLoad={({ target: map }) => {
          console.log("[MapComponent] Map Loaded");
          // Signal that the map is live so map-dependent effects (z-order) run.
          setMapReady(true);
          // Force resize to sync canvas with container
          map.resize();
          try {
            addMissingImages(map);

            // Safely handle terrain layer only if it exists
            if (terrainLayer && terrainLayer.id) {
              if (!map.getSource(terrainLayer.id)) {
                try {
                  map.addSource(terrainLayer.id, {
                    type: terrainLayer.type,
                    url: terrainLayer.url,
                    tileSize: terrainLayer.options.tileSize
                  });
                  map.setTerrain({
                    source: terrainLayer.id,
                    exaggeration: config.terrainExaggeration !== undefined ? config.terrainExaggeration : terrainLayer.options.exaggeration
                  });
                  console.log("[MapComponent] Terrain initialized");
                } catch (terrainError) {
                  console.warn("[MapComponent] Failed to add terrain source:", terrainError);
                }
              }
            } else {
              console.log("[MapComponent] No terrain layer configuration available yet");
            }
          } catch (error) {
            console.error("[MapComponent] Error in map onLoad logic:", error);
          } finally {
            // ALWAYS signal that the map has initialized, even if terrain failed
            setCommonMapState({
              hasMapInitialize: true
            });
          }
        }}
      >
        <Layers mapLibreLayers={mapLibreLayers} />
        {showLayerPanel && (
          <Box
            sx={{
              position: "absolute",
              bottom: "40px",
              right: "80px"
            }}
          >
            <LayersPanel layers={layers} cmTableLayers={cmTableMapLayers} boundaryType={orgBoundaryType} />
          </Box>
        )}
        <WmsLegendOverlay />

        {/* Processing Layer Loading Spinner */}
        {(isCmTableLoading || isMapBboxDebouncing || isSpatialDatasetLoading) && (
          <Box
            sx={{
              position: "absolute",
              top: 14,
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 1000,
              display: "flex",
              alignItems: "center",
              pointerEvents: "none"
            }}
          >
            <Paper
              elevation={3}
              sx={{
                padding: "8px 16px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-around",
                borderRadius: "20px",
                backgroundColor: "rgba(255, 255, 255, 0.9)",
                backdropFilter: "blur(4px)",
                gap: "5px"
              }}
            >
              <CircularProgress size={18} thickness={5} />
              <Typography variant='body2' sx={{ fontWeight: 600, color: "text.primary" }}>
                Processing Layer...
              </Typography>
            </Paper>
          </Box>
        )}

        {/* Popups, Legends, and Panels */}
        {/* User Location Marker */}
        {userLocation && (
          <Marker longitude={userLocation.longitude} latitude={userLocation.latitude} anchor='center'>
            <Box
              sx={{
                width: 16,
                height: 16,
                borderRadius: "50%",
                backgroundColor: "#00bfff",
                border: "2px solid white",
                boxShadow: "0 2px 8px rgba(0,191,255,0.4)",
                position: "relative",
                animation: "bluePulse 2s ease-in-out infinite",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  backgroundColor: "rgba(0, 191, 255, 0.4)",
                  animation: "bluePulseRing 2s ease-in-out infinite"
                },
                "@keyframes bluePulse": {
                  "0%": {
                    transform: "scale(1)",
                    opacity: 1
                  },
                  "50%": {
                    transform: "scale(1.1)",
                    opacity: 0.8
                  },
                  "100%": {
                    transform: "scale(1)",
                    opacity: 1
                  }
                },
                "@keyframes bluePulseRing": {
                  "0%": {
                    transform: "translate(-50%, -50%) scale(0.8)",
                    opacity: 0.8
                  },
                  "100%": {
                    transform: "translate(-50%, -50%) scale(2)",
                    opacity: 0
                  }
                }
              }}
              title={`Your location (±${Math.round(userLocation.accuracy || 0)}m)`}
            />
          </Marker>
        )}

        {/* Coordinate Search Pin — placed by search bar lat/lng input, dismissed on click */}
        {coordinateSearchMarker && (
          <Marker
            longitude={coordinateSearchMarker.lng}
            latitude={coordinateSearchMarker.lat}
            anchor='bottom'
            onClick={() => setCoordinateSearchMarker(null)}
          >
            <Box
              sx={{
                cursor: "pointer",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.4))"
              }}
              title={`${coordinateSearchMarker.lat}, ${coordinateSearchMarker.lng} — click to dismiss`}
            >
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  backgroundColor: "#e53935",
                  border: "2px solid white"
                }}
              />
              <Box
                sx={{
                  width: 2,
                  height: 10,
                  backgroundColor: "#e53935"
                }}
              />
            </Box>
          </Marker>
        )}

        {popupType === "media-viewer" && mediaMarkerCoords && !isFullViewerOpen && (
          <Popup
            key={`media-popup-${selectedEntityId}`}
            longitude={mediaMarkerCoords.lng}
            latitude={mediaMarkerCoords.lat}
            offset={[0, -16]}
            anchor='bottom'
            closeOnClick={false}
            maxWidth='500px'
            onClose={closePopup}
            className='react-popup'
            subpixelPositioning
          >
            <Box
              sx={{
                maxWidth: 480,
                minWidth: 200,
                maxHeight: "80vh",
                overflowY: "auto",
                "& .MuiCard-root": { borderRadius: "8px" }
              }}
            >
              <ReactivePopupContent
                entityId={selectedEntityId as string | number}
                entityName={selectedEntity?.name ?? ""}
                endpoint={selectedEndpoint as ApiEndpoint}
                onBack={closePopup}
              />
            </Box>
          </Popup>
        )}

        {/* React Popup for showing popup-data */}
        <MapPopup
          open={isOpen && popupType === "geo-entity" && !!popupCoords}
          popupData={{
            entityId: selectedEntity?.id ?? "",
            entityName: selectedEntity?.name ?? "",
            endpoint: selectedEndpoint as ApiEndpoint,
            coordinates: popupCoords ? [popupCoords.lng, popupCoords.lat] : [0, 0]
          }}
          onClose={closePopup}
        />

        {/* Attributes of a clicked vector tile feature — no entity behind it, so this
            renders straight from the click event rather than through the entity popup. */}
        <FeatureInfoPopup
          open={popupType === "feature-info"}
          coords={storedCoords}
          features={inspectedFeatures}
          activeIndex={inspectedIndex}
          sourceName={inspectedSourceName}
          onStep={stepFeature}
          onClose={handleCloseFeatureInfo}
        />

        {/* Spatial dataset overlays — COG and PMTiles rendered directly here
            (excluded from useMapLibreLayers to avoid source-rebuild freeze on selection).
            Each Source is UNMOUNTED when that overlay is hidden (not just visibility:none)
            so that MapLibre cancels in-flight tile requests. */}
        {visibleSpatialOverlays.map((l) => {
          if (l.type === "cog") {
            return (
              // @ts-ignore — react-map-gl types don't expose `url` on raster source
              <Source key={l.id} id={l.id} type='raster' url={`cog://${l.url}`} tileSize={(l as CogLayerConfig).options?.tileSize || 256}>
                <Layer
                  id={l.id}
                  type='raster'
                  paint={{
                    "raster-opacity": (l as CogLayerConfig).options?.opacity ?? 1,
                    "raster-fade-duration": 300,
                    ...(l as CogLayerConfig).rasterPaint
                  }}
                />
              </Source>
            );
          }

          if (l.type === "pmtiles") {
            const pl = l as PMTilesLayerConfig;
            if (pl.layerFormat === "raster") {
              return (
                // @ts-ignore
                <Source key={pl.id} id={pl.id} type='raster' url={`pmtiles://${pl.url}`} tileSize={pl.options?.tileSize || 256}>
                  <Layer
                    id={pl.id}
                    type='raster'
                    paint={{ "raster-opacity": pl.options?.opacity ?? 1, "raster-fade-duration": 300, ...pl.rasterPaint }}
                  />
                </Source>
              );
            }
            // Vector PMTiles use the established one-source/one-layer contract.
            // GeoStyler is adapted to this shape in toLayerConfig; unlike CM
            // table GeoJSON, a spatial upload has one geometry family.
            const layerType = pl.style?.type || "fill";
            const paint = pl.style?.paint || {};
            return (
              <Source key={pl.id} id={pl.id} type='vector' url={`pmtiles://${pl.url}`}>
                <Layer
                  id={pl.id}
                  type={layerType as any}
                  source-layer={pl.sourceLayer || "layer"}
                  {...(pl.style?.filter ? { filter: pl.style.filter } : {})}
                  {...(pl.style?.layout ? { layout: pl.style.layout } : {})}
                  paint={paint}
                  minzoom={pl.style?.minzoom ?? pl.options?.minZoom ?? 0}
                  maxzoom={pl.style?.maxzoom ?? pl.options?.maxZoom ?? 24}
                />
              </Source>
            );
          }

          return null;
        })}
      </Map>

      {/* Right-click context menu */}
      <ContextMenu contextMenu={contextMenu} onClose={handleClose} onCopy={handleCopyCoordinates} />
    </Box>
  );
};

export default React.memo(MapComponent);
